import { FC, useEffect, useState } from 'react'
import { useRecordContext } from 'react-admin'
import { CircularProgress, Typography } from '@mui/material'
import { getJson, getUrl } from '../providers/data'
import { Auth0UserOut } from '../types/apiRequests'

interface Auth0UserFieldProps {
  source?: string
  userId?: string
  display?: 'name' | 'email' | 'nameEmail'
  label?: string
}

const Auth0UserField: FC<Auth0UserFieldProps> = ({
  source = 'auth0_user_id',
  userId: userIdProp,
  display = 'nameEmail',
}) => {
  const record = useRecordContext()
  // eslint-disable-next-line security/detect-object-injection
  const userId = userIdProp ?? (record ? (record[source] as string | undefined) : undefined)

  const [user, setUser] = useState<Auth0UserOut | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!userId) return
    const abortController = new AbortController()
    setLoading(true)
    getJson<Auth0UserOut[]>(
      `${getUrl()}/auth0_user?q=${encodeURIComponent(userId)}`,
      { signal: abortController.signal },
    )
      .then((results) => {
        if (!abortController.signal.aborted) {
          setUser(results.find((u) => u.user_id === userId) ?? null)
        }
      })
      .catch(() => { if (!abortController.signal.aborted) setUser(null) })
      .finally(() => { if (!abortController.signal.aborted) setLoading(false) })
    return () => abortController.abort()
  }, [userId])

  if (!userId) return <Typography variant="body2" color="text.secondary">—</Typography>
  if (loading) return <CircularProgress size={14} />

  if (!user) {
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {userId}
      </Typography>
    )
  }

  if (display === 'name') {
    return <Typography variant="body2">{user.name ?? user.email ?? userId}</Typography>
  }
  if (display === 'email') {
    return (
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
        {user.email ?? userId}
      </Typography>
    )
  }

  return (
    <span>
      <Typography variant="body2" component="span">{user.name ?? user.email ?? userId}</Typography>
      {user.name && user.email && (
        <Typography variant="body2" component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>
          ({user.email})
        </Typography>
      )}
    </span>
  )
}

export default Auth0UserField
